import { useEffect, useState } from 'react'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import styled from 'styled-components'
import { useOptionalOfferPageAssistantSurfaceRegistryContext } from 'tripPlanner/assistant/surfaces/offerPage/OfferPageAssistantSurfaceRegistry'
import type {
  AssistantSurfaceTargetPrepareStep,
  SurfaceFocusMode,
} from 'tripPlanner/assistant/surfaceFocus/surfaceFocusTypes'

/** Sticky-header/nav clearance so a focused row lands below the fixed page chrome. */
const DEFAULT_FOCUS_SCROLL_OFFSET = 80

// Only the parent/sibling is a focus target; the anchor must not occupy a grid/flex item.
const FocusAnchor = styled(ScrollAnchor)`
  position: absolute;
`

interface Props {
  /** The canonical snapshot target id this anchor represents (e.g. `room-package:<roomId>:<rateId>`). */
  targetId: string
  focusMode?: SurfaceFocusMode
  prepareSteps?: Array<AssistantSurfaceTargetPrepareStep>
  scrollOffset?: number
  /**
   * Which visible element the anchor resolves to as the focus/highlight target: its containing
   * element (`parent`, default — anchor rendered as the row's first child) or the element rendered
   * immediately after it (`next-sibling` — anchor rendered just before the row, e.g. inside a grid
   * where an extra child would disrupt layout).
   */
  anchorRelation?: 'parent' | 'next-sibling'
  /**
   * Reuses an already-mounted zero-height anchor node instead of rendering another `ScrollAnchor`
   * -- for rows that already mount one for an unrelated purpose (e.g. a legacy hash-based scroll
   * target), so this component doesn't add a second, purely duplicative sibling to reach the same
   * parent/next-sibling element. The resolved *target* element is still derived the same way
   * (`anchorRelation`) from whichever anchor node is in play.
   */
  anchorElement?: HTMLElement | null
}

/**
 * Registers the assistant focus target for a package/room/rate row. Registers the resolved visible
 * row element (not the zero-height anchor itself) so both the scroll and the highlight land on the
 * visible row. Self-contained (registers on mount, unregisters on unmount) and a no-op when no
 * assistant surface registry is mounted. Callers must build `targetId` with the same helpers the
 * snapshot builder uses so the ids match exactly.
 */
export default function OfferPageFocusAnchor({
  targetId,
  focusMode = 'scroll',
  prepareSteps,
  scrollOffset = DEFAULT_FOCUS_SCROLL_OFFSET,
  anchorRelation = 'parent',
  anchorElement: externalAnchor,
}: Props) {
  const registry = useOptionalOfferPageAssistantSurfaceRegistryContext()
  const [ownAnchor, setOwnAnchor] = useState<HTMLDivElement | null>(null)
  const anchor = externalAnchor !== undefined ? externalAnchor : ownAnchor

  useEffect(() => {
    const element = anchorRelation === 'next-sibling'
      ? anchor?.nextElementSibling
      : anchor?.parentElement
    if (!registry || !(element instanceof HTMLElement)) return undefined

    return registry.registerFocusTarget(targetId, {
      element,
      focusMode,
      availability: 'rendered',
      prepareSteps,
      getScrollOffset: () => scrollOffset,
    })
  }, [registry, anchor, targetId, focusMode, prepareSteps, scrollOffset, anchorRelation])

  if (externalAnchor !== undefined) return null
  return <FocusAnchor ref={setOwnAnchor} />
}
